import { Grid, Stack, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import * as emailReportApi from '../../api/app/emailReportApi';
import * as api from '../../api/app/shiftsApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { CheckBox } from '../../components/core/CheckBox/CheckBox';
import { Loader } from '../../components/core/Loader/Loader';
import { SelectBox } from '../../components/core/SelectBox/SelectBox';
import { SelectBoxAutocomplete } from '../../components/core/SelectBox/SelectBoxAutocomplete';
import { useTranslation } from '../../contexts/translation';
import {
  EmailReportInputs,
  reportDaysOfWeek,
  reportTypes,
  TimeZone,
} from '../../types/app/emailReport.types';
import { Shift } from '../../types/app/shifts.types';
import { dictionary } from '../../utils/core/dictionary';
interface Props {
  onSubmit: SubmitHandler<EmailReportInputs>;
  defaultValues?: EmailReportInputs;
  onBack: any;
  isReadOnly?: boolean;
  handleUnMarkAsSend?: () => void;
  showUnmarkButton?: boolean;
  setSelectedTz: any;
  selectedTz: any;
}

export function EmailReportForm({
  onSubmit,
  defaultValues,
  onBack,
  isReadOnly,
  handleUnMarkAsSend,
  showUnmarkButton,
  setSelectedTz,
  selectedTz,
}: Props) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<EmailReportInputs>();

  const { data, isLoading, isError } = useQuery<Shift[]>('shifts', api.getShiftList);
  const {
    data: timeZones,
    isLoading: timeZonesIsLoading,
    isError: timeZonesIsError,
  } = useQuery<string[]>(['timeZones'], () => emailReportApi.getTimeZones());
  const [selectedReportType, setSelectedReportType] = useState<string | undefined>('');
  //const [startOfWeek, setStartOfWeek] = useState<string | undefined>('');

  const getTimeZones = () => {
    let timeZonesTmp: TimeZone[] = [];
    if (timeZones) {
      timeZones.forEach((tz: string) => {
        timeZonesTmp = [...timeZonesTmp, { id: tz, name: tz }];
      });
      return timeZonesTmp;
    }
  };

  useEffect(() => {
    if (!defaultValues) return;
    setSelectedReportType(defaultValues.type);
    //setStartOfWeek(defaultValues.startOfWeek);
  }, [defaultValues]);

  useEffect(() => {
    reset({
      ...defaultValues,
      timeZone: defaultValues?.timeZone ? defaultValues?.timeZone : selectedTz,
    });
  }, [defaultValues, reset]);

  if (isError || timeZonesIsError) return <ApiError />;
  if (isLoading || timeZonesIsLoading || !data || !timeZones) return <Loader />;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SelectBox
                  label={t('page.email-report.edit-input-label-1')}
                  items={reportTypes}
                  valueKey={'code'}
                  labelKey={'name'}
                  onChange={(event: any) => setSelectedReportType(event)}
                  controllerProps={{
                    name: 'type',
                    control: control,
                    defaultValue: defaultValues?.type,
                  }}
                  errors={errors}
                  isReadOnly={isReadOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label={t('page.email-report.edit-input-label-2')}
                  {...register('name', {
                    required: t('page.email-report.validation-required-name'),
                  })}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label={t('page.email-report.edit-input-label-3')}
                  {...register('emailSubject', {
                    required: t('page.email-report.validation-required-subject'),
                  })}
                  error={Boolean(errors.emailSubject)}
                  helperText={errors.emailSubject?.message}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} mb={1}>
                <TextField
                  fullWidth
                  label={t('page.email-report.edit-input-label-4')}
                  {...register('dateTimeFormat')}
                  error={Boolean(errors.emailSubject)}
                  helperText={'Example: d.M.yy HH:mm'}
                  disabled={isReadOnly}
                />
              </Grid>
              {/* For Weekly performance var 2 the shift is shown as "Start shift" grouped with the interval inputs */}
              {selectedReportType !== 'WeeklyPerformanceVar2' && (
                <Grid item xs={12} mb={1}>
                  <SelectBoxAutocomplete
                    isRequired={true}
                    label={t('page.email-report.edit-input-label-5')}
                    items={data}
                    valueKey={'id'}
                    labelKey={'name'}
                    controllerProps={{
                      name: 'shiftId',
                      control: control,
                    }}
                    errors={errors}
                    readOnly={isReadOnly}
                  />
                </Grid>
              )}
              {timeZones && (
                <Grid item xs={12}>
                  <SelectBoxAutocomplete
                    isRequired={true}
                    label={t('page.email-report.edit-input-label-6')}
                    items={getTimeZones()!}
                    valueKey={'name'}
                    labelKey={'name'}
                    onChange={(value: any) => {
                      setSelectedTz(value);
                    }}
                    controllerProps={{
                      name: 'timeZone',
                      control: control,
                    }}
                    errors={errors}
                    readOnly={isReadOnly}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={8}>
            <Grid item xs={12}>
              <Typography variant="body1">Send on days: </Typography>
              <CheckBox
                label={dictionary.MONDAY}
                controllerProps={{
                  name: 'sendOnMonday',
                  control: control,
                }}
                errors={errors}
                readOnly={isReadOnly}
              />
              <CheckBox
                label={dictionary.TUESDAY}
                controllerProps={{
                  name: 'sendOnTuesday',
                  control: control,
                }}
                errors={errors}
                readOnly={isReadOnly}
              />
              <CheckBox
                label={dictionary.WEDNESDAY}
                controllerProps={{
                  name: 'sendOnWednesday',
                  control: control,
                }}
                errors={errors}
                readOnly={isReadOnly}
              />
              <CheckBox
                label={dictionary.THURSDAY}
                controllerProps={{
                  name: 'sendOnThursday',
                  control: control,
                }}
                errors={errors}
                readOnly={isReadOnly}
              />
              <CheckBox
                label={dictionary.FRIDAY}
                controllerProps={{
                  name: 'sendOnFriday',
                  control: control,
                }}
                errors={errors}
                readOnly={isReadOnly}
              />
              <CheckBox
                label={dictionary.SATURDAY}
                controllerProps={{
                  name: 'sendOnSaturday',
                  control: control,
                }}
                errors={errors}
                readOnly={isReadOnly}
              />
              <CheckBox
                label={dictionary.SUNDAY}
                controllerProps={{
                  name: 'sendOnSunday',
                  control: control,
                }}
                errors={errors}
                readOnly={isReadOnly}
              />
            </Grid>
            {selectedReportType === 'DailyPerformance' && (
              <Grid item xs={12} md={4} mt={1}>
                <TextField
                  fullWidth
                  label={dictionary.NUMBER_OF_DAYS}
                  {...register('intParam1')}
                  error={Boolean(errors.intParam1)}
                  helperText={errors.intParam1?.message}
                  type="number"
                  disabled={isReadOnly}
                />
              </Grid>
            )}
            {selectedReportType === 'WeeklyPerformance' && (
              <Grid item xs={12} md={4} mt={1}>
                <TextField
                  fullWidth
                  label={dictionary.NUMBER_OF_WEEKS}
                  {...register('intParam1')}
                  defaultValue={defaultValues?.intParam1 || 0}
                  error={Boolean(errors.intParam1)}
                  helperText={errors.intParam1?.message}
                  type="number"
                  disabled={isReadOnly}
                />
              </Grid>
            )}
            {selectedReportType === 'WeeklyPerformance' && (
              <Grid item xs={12} md={4} mt={2}>
                <SelectBox
                  label={dictionary.START_OF_WEEK}
                  {...register('startOfWeek', {
                    required: t('page.email-report.validation-required-startofweek'),
                  })}
                  items={reportDaysOfWeek}
                  valueKey={'code'}
                  labelKey={'name'}
                  controllerProps={{
                    name: 'startOfWeek',
                    control: control,
                    defaultValue: defaultValues?.startOfWeek ?? '',
                  }}
                  errors={errors}
                  isReadOnly={isReadOnly}
                />
              </Grid>
            )}
            {selectedReportType === 'WeeklyPerformanceVar2' && (
              <Grid item xs={12} mt={1}>
                <Typography variant="body1" mb={1}>
                  {dictionary.REPORT_INTERVAL}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={dictionary.NUMBER_OF_WEEKS}
                      {...register('intParam1')}
                      defaultValue={defaultValues?.intParam1 || 0}
                      error={Boolean(errors.intParam1)}
                      helperText={errors.intParam1?.message}
                      type="number"
                      disabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12} />
                  <Grid item xs={12} md={3}>
                    <SelectBox
                      label={dictionary.START_OF_WEEK}
                      {...register('startOfWeek', {
                        required: t('page.email-report.validation-required-startofweek'),
                      })}
                      items={reportDaysOfWeek}
                      valueKey={'code'}
                      labelKey={'name'}
                      controllerProps={{
                        name: 'startOfWeek',
                        control: control,
                        defaultValue: defaultValues?.startOfWeek ?? '',
                      }}
                      errors={errors}
                      isReadOnly={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectBoxAutocomplete
                      isRequired={true}
                      label={dictionary.START_SHIFT}
                      items={data}
                      valueKey={'id'}
                      labelKey={'name'}
                      controllerProps={{
                        name: 'shiftId',
                        control: control,
                      }}
                      errors={errors}
                      readOnly={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectBox
                      label={dictionary.END_OF_WEEK}
                      {...register('endOfWeek')}
                      items={reportDaysOfWeek}
                      valueKey={'code'}
                      labelKey={'name'}
                      controllerProps={{
                        name: 'endOfWeek',
                        control: control,
                        defaultValue: defaultValues?.endOfWeek ?? '',
                      }}
                      errors={errors}
                      isReadOnly={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SelectBoxAutocomplete
                      label={dictionary.END_SHIFT}
                      items={data}
                      valueKey={'id'}
                      labelKey={'name'}
                      controllerProps={{
                        name: 'endShiftId',
                        control: control,
                      }}
                      errors={errors}
                      readOnly={isReadOnly}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item mt={2}>
            <Stack spacing={2} direction="row">
              <Button type="button" variant="outlined" onClick={onBack}>
                {t('general.action-buttons.back')}
              </Button>
              {!isReadOnly && (
                <>
                  <Button type="submit" variant="contained" color="success">
                    {t('general.action-buttons.save')}
                  </Button>
                  {showUnmarkButton && (
                    <Button onClick={handleUnMarkAsSend} variant={'contained'}>
                      {t('page.email-report.edit-button-unmark')}
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
